import {
  Stack,
  InputLabel,
  useTheme,
  Theme,
  Box,
  Typography,
  BoxProps,
} from "@mui/material";
import { useTranslations } from "next-intl";
import {
  FieldValues,
  SelectElement,
  SelectElementProps,
  useFormContext,
  useWatch,
} from "react-hook-form-mui";
import { Iconify } from "..";

interface CheckedLabelProps extends BoxProps {
  isActive: boolean;
  label: any;
}

export const CheckedLabel = ({
  isActive,
  label,
  ...props
}: CheckedLabelProps) => {
  const t = useTranslations();
  const theme = useTheme();

  return (
    <Box
      minWidth="5rem"
      width="100%"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      {...props}
    >
      <Typography
        color={isActive ? theme.palette.primary.main : "text.primary"}
        className="label"
      >
        {t(label)}{" "}
      </Typography>
      {isActive && <Iconify icon="gravity-ui:check" fontSize="1.4rem" />}
    </Box>
  );
};

export const FormSelect = ({
  label,
  disabled,
  InputProps,
  options,
  SelectLabelProps,

  ...props
}: SelectElementProps<FieldValues> & { SelectLabelProps?: BoxProps }) => {
  const t = useTranslations();
  const theme = useTheme<Theme>();

  const { control } = useFormContext();
  const value = useWatch({ control, name: props.name });
  const styledOptions = options?.map((item) => ({
    ...item,
    label: (
      <CheckedLabel
        {...SelectLabelProps}
        isActive={value === item.id}
        label={item.label}
      />
    ),
  }));

  return (
    <Stack spacing={1} sx={{ opacity: disabled ? 0.7 : 1 }}>
      {label && (
        <InputLabel
          sx={{ fontWeight: 500, lineHeight: 1, textTransform: "capitalize" }}
        >
          {typeof label === "string" ? t(label) : label}
        </InputLabel>
      )}
      <SelectElement
        {...props}
        options={styledOptions}
        disabled={disabled}
        SelectProps={{
          displayEmpty: true,
          MenuProps: {
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "right",
            },
            transformOrigin: {
              vertical: -10,
              horizontal: "right",
            },
            MenuListProps: {
              sx: {
                minWidth: "5rem",
                boxShadow: theme.shadows[2],
              },
            },
            disablePortal: true,
            sx: {
              mariginTop: "1rem",
              textTransform: "capitalize",
              "&& .Mui-selected": {
                backgroundColor: "transparent",
                color: theme.palette.primary.main,
              },
            },
            PaperProps: {
              sx: {
                borderRadius: 2,
                elevation: 0,
              },
            },
          },
        }}
        InputProps={{
          ...InputProps,
          sx: {
            borderRadius: "0.5rem",
            padding: 0,
            height: "50px",
            color: "transparent",
            "& .MuiSelect-select .label": {
              color: "text.primary",
            },
            ...(InputProps?.sx ?? {}),
          },
        }}
      />
    </Stack>
  );
};
